import React from 'react';
import { FaqItemType } from '@/types';
import { SITE_NAME } from '@/config';
import {
    GemIcon, FilmIcon, ServerIcon, FilterIcon, TvIcon, LanguageIcon,
    VideoIcon, SlidersIcon, FacebookIcon, WhatsAppIcon, TelegramIcon,
    XTwitterIcon, PinterestIcon, ThreadsIcon, LinkedInIcon, MessengerIcon,
    CommentsIcon, RedditIcon, EnvelopeIcon, CommentSmsIcon, DiscordIcon, SnapchatIcon
} from '@/components/ui/Icons';

export const popularSearches = [
    'RRR',
    'Leo',
    'Joker',
    'Jawan',
    '60FPS',
    'Barbie',
    'Animal',
    'Fast X',
    'Salaar',
    'Titanic',
    'Pathaan',
    'Inception',
    'Wednesday',
    'The Matrix',
    'Oppenheimer',
    'Interstellar',
    'Gladiator II',
    'Harry Potter',
    'Breaking Bad',
    'Kalki 2898 AD',
    'Dune: Part Two',
    'The Dark Knight',
    'Stranger Things',
    'Game of Thrones',
    'K.G.F: Chapter 2',
    'Avengers: Endgame',
    'Pushpa 2: The Rule',
    'John Wick: Chapter 4',
    'Deadpool & Wolverine',
    'Spider-Man: No Way Home',
    'Avatar: The Way of Water',
    'Bahubali 2: The Conclusion',
    'Godzilla x Kong: The New Empire'
];


export const featuresData = [
    {
        icon: React.createElement(GemIcon, { className: "text-xl sm:text-3xl" }),
        title: "Premium Cinematic Quality",
        description: "Experience crystal-clear visuals in 2160p 4K UHD, BluRay REMUX, HDR10, and Dolby Vision with immersive Dolby Atmos 7.1 surround sound. We support x265 HEVC 10-bit, x264, and AV1 codecs for maximum fidelity at optimized file sizes."
    },
    {
        icon: React.createElement(FilmIcon, { className: "text-xl sm:text-3xl" }),
        title: "Vast Collection",
        description: "Access 7000+ movies and TV series across 24+ genres — spanning Bollywood, Hollywood, South Indian, East Indian, Western Indian, Western, Pollywood, Foreign, Web Series, and Asian Cinema & Dramas (Korean, Chinese, Japanese, Thai)."
    },
    {
        icon: React.createElement(ServerIcon, { className: "text-xl sm:text-3xl" }),
        title: "Lightning-Fast Cloud Servers",
        description: "Download directly from premium Google Drive, Hubcloud, Gdflix, and Filepress mirrors with zero speed throttling. Our 24/7 infrastructure is optimized for mobile-first performance and instant content delivery."
    },
    {
        icon: React.createElement(FilterIcon, { className: "text-xl sm:text-3xl" }),
        title: "Smart Content Filtering",
        description: "Browse easily by Genre, Year, Type, Age, Industry, or Quality — everything perfectly organized for quick access."
    },
    {
        icon: React.createElement(TvIcon, { className: "text-xl sm:text-3xl" }),
        title: "Multiple Content Types",
        description: "Movies, TV Shows, Mini-Series, Collections, and a dedicated 18+ Section — all in one place."
    },
    {
        icon: React.createElement(LanguageIcon, { className: "text-xl sm:text-3xl" }),
        title: "Multi-Language & Subtitles",
        description: "Enjoy Dual-Audio and Multi-Audio tracks in Hindi, English, Tamil, Telugu, Malayalam, Kannada, Punjabi, Bengali, Gujarati & more. Every release includes embedded English subtitles with optional multilingual SRT packs."
    },
    {
        icon: React.createElement(VideoIcon, { className: "text-xl sm:text-3xl" }),
        title: "Flexible Video Formats",
        description: "Choose from 2160p 4K UHD, 1440p, 1080p Full HD, 720p HD & 480p SD. Premium releases include BluRay REMUX, IMAX Enhanced, Open Matte, 3D, SDR, HDR10, Dolby Vision, and ultra-smooth 60FPS / 120FPS / 144FPS options."
    },
    {
        icon: React.createElement(SlidersIcon, { className: "text-xl sm:text-3xl" }),
        title: "User-Friendly Features",
        description: "Smooth UI/UX with a minimal, modern design, subtle motion, and built-in accessibility. Includes personal accounts, dead-link reporting, notifications, and social integration for a seamless experience."
    }
];

export const faqData: FaqItemType[] = [
    {
        question: `How do I search or request specific movies on ${SITE_NAME}?`,
        answer: `You can quickly discover your favorite films by typing the title, actor, or genre into our high-speed search bar at the top of the homepage. For any specific film or series not currently listed, join our official Telegram channel and submit your request. Our active release group uploads requested content in record time.`
    },
    {
        question: `What video resolutions and formats does ${SITE_NAME} support?`,
        answer: `We offer an unparalleled cinematic viewing experience by hosting files in resolutions up to 4K UHD, including 2160p, 2K, 1440p, 1080p Full HD, and 720p HD. Our collection includes high-fidelity formats like BluRay REMUX, Web-DL, SDR, HDR10, Dolby Vision, IMAX Enhanced, Open Matte, and ultra-smooth 60FPS / 120FPS releases.`
    },
    {
        question: "Where are the media files hosted, and are downloads fast?",
        answer: "All downloads are hosted on premium, secure high-speed Google Drive, Hubcloud, Gdflix, and Filepress servers. This ensures maximum bandwidth utilization, providing lightning-fast direct download speeds with zero ISP throttling or speed limits."
    },
    {
        question: `Do I need a VPN to access ${SITE_NAME}?`,
        answer: `No registration or VPN is strictly required to browse our platform. However, if your local internet service provider (ISP) restricts access to entertainment indexers in your country, using a secure VPN service or Cloudflare WARP (1.1.1.1) will instantly bypass any network blocks safely.`
    },
    {
        question: "Are there multi-language dual-audio movies and subtitles?",
        answer: "Yes, a massive portion of our library features Dual-Audio and Multi-Audio files. You can enjoy international and regional Indian content in English, Hindi, Tamil, Telugu, Malayalam, Kannada, Punjabi, and Bengali, complete with embedded multilingual subtitle options."
    },
    {
        question: `Can I download OTT Web Series and television shows from ${SITE_NAME}?`,
        answer: `Absolutely. We index complete season packs for major OTT platforms, including Netflix, Amazon Prime Video, Disney+ Hotstar, Apple TV+, Zee5, and SonyLIV. Web series are available in high-definition quality with Dual-Audio streams and embedded English subtitles.`
    },
    {
        question: `Is ${SITE_NAME} safe to use, and do I need to register?`,
        answer: `Yes, ${SITE_NAME} is 100% free and requires absolutely no registration, sign-up, or installation. Unlike other platforms that plague users with aggressive malware or pop-up ads, we maintain a clean, secure gateway to keep your browsing experience safe and fast.`
    },
    {
        question: `What should I do if a Google Drive link shows a "Download Quota Exceeded" error?`,
        answer: `When a file receives extremely high traffic, Google Drive may temporarily limit downloads. If you encounter a quota limit error, simply select our alternate Hubcloud, Gdflix, or Filepress mirrors listed on the download page for immediate access.`
    }
];

export const socialLinks = (shareUrl: string, shareTitle: string, shareDescription: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedDescription = encodeURIComponent(shareDescription);
    return [
        { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, style: { background: 'linear-gradient(45deg, #1877f2, #3b5998)' }, icon: React.createElement(FacebookIcon, { className: "text-2xl" }) },
        { name: 'WhatsApp', href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`, style: { background: 'linear-gradient(45deg, #25D366, #128C7E)' }, icon: React.createElement(WhatsAppIcon, { className: "text-2xl" }) },
        { name: 'Telegram', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`, style: { background: 'linear-gradient(45deg, #0088cc, #0099ff)' }, icon: React.createElement(TelegramIcon, { className: "text-2xl" }) },
        { name: 'X', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`, style: { background: 'linear-gradient(45deg, #000000, #333333)' }, icon: React.createElement(XTwitterIcon, { className: "text-2xl" }) },
        { name: 'Pinterest', href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedDescription}`, style: { background: 'linear-gradient(45deg, #E60023, #BD081C)' }, icon: React.createElement(PinterestIcon, { className: "text-2xl" }) },
        { name: 'Threads', href: `https://www.threads.net/intent/post?text=${encodedTitle}%20${encodedUrl}`, style: { background: 'linear-gradient(45deg, #1a1a1a, #4a4a4a)' }, icon: React.createElement(ThreadsIcon, { className: "text-2xl" }) },
    ];
};

export const otherLinks = (shareUrl: string, shareTitle: string, _shareDescription: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    return [
        { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, style: { background: 'linear-gradient(45deg, #0A66C2, #0077B5)' }, icon: React.createElement(LinkedInIcon, { className: "text-2xl" }) },
        { name: 'Messenger', href: `fb-messenger://share/?link=${encodedUrl}`, style: { background: 'linear-gradient(45deg, #00B2FF, #006AFF)' }, icon: React.createElement(MessengerIcon, { className: "text-2xl" }) },
        { name: 'Arattai', href: `https://arattai.in/app/share?text=${encodedTitle}%20${encodedUrl}`, style: { background: 'linear-gradient(45deg, #FFC107, #FFD54F)' }, icon: React.createElement(CommentsIcon, { className: "text-2xl text-black" }) },
        { name: 'Reddit', href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`, style: { background: 'linear-gradient(45deg, #FF4500, #FF6314)' }, icon: React.createElement(RedditIcon, { className: "text-2xl" }) },
        { name: 'Email', href: `mailto:?subject=${encodedTitle}&body=Check this out: ${encodedUrl}`, style: { background: 'linear-gradient(45deg, #6B46C1, #9F7AEA)' }, icon: React.createElement(EnvelopeIcon, { className: "text-2xl" }) },
        { name: 'Discord', href: `https://discord.com/channels/@me`, style: { background: 'linear-gradient(45deg, #5865F2, #4752C4)' }, icon: React.createElement(DiscordIcon, { className: "text-2xl" }) },
        { name: 'Snapchat', href: `https://www.snapchat.com/share?link=${encodedUrl}`, style: { background: 'linear-gradient(45deg, #FFFC00, #FEE500)' }, icon: React.createElement(SnapchatIcon, { className: "text-2xl text-black" }) },
        { name: 'SMS', href: `sms:?&body=${encodedTitle}%20${encodedUrl}`, style: { background: 'linear-gradient(45deg, #34C759, #28a745)' }, icon: React.createElement(CommentSmsIcon, { className: "text-2xl" }) },
    ];
};

export const MODAL_IDS = {
    TIPS: 'tips',
    ABOUT: 'about',
    PRIVACY: 'privacy',
    TERMS: 'terms',
    CONTACT: 'contact',
    SHARE: 'share',
    SETTINGS: 'settings',
} as const;